import {
  Button,
  Card,
  Flex,
  FormContainer,
  Icon,
  notify,
  Title,
} from '@wyz/ui';
import { get } from 'lodash-es';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import { TextField } from '~/common/components';
import { Trans, useApplicationContext, useI18n } from '~/common/context';
import { useManageSupplierContainerOrders } from '~/container-order/api';
import type { IContainerOrder } from '~/common/types';

interface CommentForm {
  comment: string;
}

type CommentCardMode = 'READ_ONLY' | 'EDIT';

type CommentCardContextValue = {
  mode: CommentCardMode;
  comment: string;
  titleKey: React.ReactNode;
  methods: ReturnType<typeof useForm<CommentForm>>;
  isUpdating: boolean;
  onEditToggle: () => void;
  onCancelEdit: () => void;
  onSubmit: (data: CommentForm) => void;
  setIsUpdating: (isUpdating: boolean) => void;
};

const CommentCardContext = React.createContext<
  CommentCardContextValue | undefined
>(undefined);

const useCommentCardContext = () => {
  const context = React.useContext(CommentCardContext);
  if (!context) {
    throw new Error(
      'CommentCard compound components must be used within CommentCard',
    );
  }
  return context;
};

interface CommentCardProps {
  titleKey: React.ReactNode;
  order: Pick<IContainerOrder, 'comments' | 'id'>;
  commentKey: 'comments.comment' | 'comments.supplierComment';
  children: React.ReactNode;
}

const CommentCard: React.FC<CommentCardProps> = ({
  titleKey,
  children,
  order,
  commentKey,
}) => {
  const [mode, setMode] = React.useState<CommentCardMode>('READ_ONLY');
  const [isUpdating, setIsUpdating] = React.useState(false);
  const { auth } = useApplicationContext();

  const methods = useForm<CommentForm>({
    defaultValues: {
      comment: get(order, commentKey, ''),
    },
  });

  React.useEffect(() => {
    methods.reset({ comment: get(order, commentKey, '') });
  }, [order, methods, commentKey]);

  const handleCancelEdit = () => {
    methods.reset({ comment: get(order, commentKey, '') });
    setMode('READ_ONLY');
  };

  const handleEditToggle = () => {
    setMode(mode === 'EDIT' ? 'READ_ONLY' : 'EDIT');
  };

  const containerOrderQuery = useManageSupplierContainerOrders({
    patch: {
      enabled: true,
    },
    get: {
      enabled: false,
      args: {
        id: '',
      },
    },
    context: {
      userId: auth.userId as string,
    },
  });

  const handleSubmit = (data: CommentForm) => {
    containerOrderQuery.patch.mutate(
      {
        id: order.id,
        key: commentKey,
        value: data.comment,
      },
      {
        onSuccess: () => {
          notify.success(<Trans i18nKey='app.common.messages.success' />, {
            id: 'toast-success-comment-update',
          });
          setMode('READ_ONLY');
        },
        onError: () => {
          notify.error(<Trans i18nKey='app.common.messages.error' />, {
            id: 'toast-error-comment-update',
          });
        },
      },
    );
  };
  const contextValue: CommentCardContextValue = {
    mode,
    comment: get(order, commentKey, ''),
    titleKey,
    methods,
    isUpdating,
    onEditToggle: handleEditToggle,
    onCancelEdit: handleCancelEdit,
    onSubmit: handleSubmit,
    setIsUpdating,
  };

  return (
    <CommentCardContext.Provider value={contextValue}>
      <Card className='mt-section'>{children}</Card>
    </CommentCardContext.Provider>
  );
};

const CommentCardHeader: React.FC = () => {
  const { titleKey } = useCommentCardContext();

  return (
    <Card.Header>
      <Flex className='space-x-1' alignItems='center'>
        <Icon icon='chat-line' size='xs' className='text-royal-blue' />
        <Title heading='lg' weight='bold' wrap>
          {titleKey}
        </Title>
      </Flex>
    </Card.Header>
  );
};

const CommentCardContent: React.FC = () => {
  const { t } = useI18n();
  const { mode, comment, methods, onSubmit } = useCommentCardContext();

  return (
    <Card.Body>
      {mode === 'EDIT' ? (
        <FormContainer
          id='comment-form'
          methods={methods}
          submitHandler={onSubmit}
        >
          <TextField
            name='comment'
            type='text'
            as='textarea'
            label=''
            placeholder={t('app.common.form.add_comment')}
            rows={4}
          />
        </FormContainer>
      ) : (
        <div className='bg-neutral-grey-1 p-2' style={{ minHeight: 100 }}>
          {comment}
        </div>
      )}
    </Card.Body>
  );
};

const CommentCardActions: React.FC = () => {
  const { mode, onEditToggle, onCancelEdit, isUpdating, methods } =
    useCommentCardContext();

  return (
    <Card.Footer>
      <hr />
      <Flex justifyContent='end' className='space-x-2'>
        {mode === 'EDIT' ? (
          <>
            <Button
              id='cancel-edit'
              variant='secondary'
              size='sm'
              onClick={onCancelEdit}
              disabled={isUpdating}
            >
              <Trans i18nKey='app.common.button.cancel' />
            </Button>
            <Button
              id='save-edit'
              type='submit'
              variant='primary'
              size='sm'
              disabled={!methods.formState.isDirty || isUpdating}
              isLoading={isUpdating}
              form='comment-form'
            >
              <Trans i18nKey='app.common.button.save' />
            </Button>
          </>
        ) : (
          <Button
            id='edit-comment'
            variant='secondary'
            size='sm'
            onClick={onEditToggle}
            fullWidth
          >
            <Trans i18nKey='app.common.buttons.update' />
          </Button>
        )}
      </Flex>
    </Card.Footer>
  );
};

type CommentCardComponent = React.FC<CommentCardProps> & {
  Content: typeof CommentCardContent;
  Header: typeof CommentCardHeader;
  Actions: typeof CommentCardActions;
};

const CommentCardPublicApi = CommentCard as CommentCardComponent;

CommentCardPublicApi.Content = CommentCardContent;
CommentCardPublicApi.Header = CommentCardHeader;
CommentCardPublicApi.Actions = CommentCardActions;

export default CommentCardPublicApi;
